import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { AUTH_TYPES, Card, Field, Row, Toggle, inputCls } from "./_shared";

/**
 * Wizard Step 1 — General info (view + edit).
 *
 * View card shows:
 *   • Description, base URL, environment, auth type, default timeout,
 *     TLS / redirect flags, tags.
 *   • Lifecycle metadata (created/updated/published/retired/last test).
 *
 * Editing is gated by ``immutable`` (published/retired connections can
 * only be cloned to a new draft) and the ``connection.edit`` permission.
 */
export default function GeneralStep({ conn, immutable, canEdit, onSaved }) {
    const [edit, setEdit] = useState(false);
    const [form, setForm] = useState({
        name: conn.name, description: conn.description || "",
        base_url: conn.base_url, environment: conn.environment,
        auth_type: conn.auth_type, default_timeout_s: conn.default_timeout_s,
        tls_required: conn.tls_required, allow_redirects: conn.allow_redirects,
        tags: (conn.tags || []).join(", "),
    });
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            const body = {
                name: form.name.trim(),
                description: form.description.trim() || null,
                base_url: form.base_url.trim(),
                environment: form.environment,
                auth_type: form.auth_type,
                default_timeout_s: Number(form.default_timeout_s),
                tls_required: form.tls_required,
                allow_redirects: form.allow_redirects,
                tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
            };
            await api.patch(`/admin/connections/${conn.id}`, body);
            toast.success("Saved");
            setEdit(false);
            onSaved();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    if (!edit) {
        return (
            <div className="space-y-3" data-testid="wizard-general-view">
                <div className="grid md:grid-cols-2 gap-3">
                    <Card title="General">
                        <Row k="Description" v={conn.description || <span className="text-white/30">—</span>} />
                        <Row k="Base URL" v={<span className="font-mono break-all">{conn.base_url}</span>} />
                        <Row k="Environment" v={conn.environment} />
                        <Row k="Auth type" v={<span className="font-mono">{(AUTH_TYPES.find((a) => a.value === conn.auth_type) || {}).label || conn.auth_type}</span>} />
                        {conn.auth_type === "sitecentric_client_key" && (
                            <Row k="Token / logout" v={<span className="font-mono text-[10px]" data-testid="general-sitecentric-paths">{conn.auth_config?.token_path} · {conn.auth_config?.logout_method} {conn.auth_config?.logout_path}</span>} />
                        )}
                        <Row k="Default timeout" v={`${conn.default_timeout_s}s`} />
                        <Row k="TLS required" v={conn.tls_required ? "yes" : "no"} />
                        <Row k="Allow redirects" v={conn.allow_redirects ? "yes" : "no"} />
                        <Row k="Tags" v={(conn.tags || []).join(", ") || <span className="text-white/30">—</span>} />
                    </Card>
                    <Card title="Lifecycle">
                        <Row k="Created" v={`${conn.created_by || "?"} · ${(conn.created_at || "").slice(0, 16).replace("T", " ")}`} />
                        <Row k="Updated" v={`${conn.updated_by || "?"} · ${(conn.updated_at || "").slice(0, 16).replace("T", " ")}`} />
                        <Row k="Published" v={conn.published_at ? `${conn.published_by} · ${conn.published_at.slice(0, 16).replace("T", " ")}` : <span className="text-white/30">—</span>} />
                        <Row k="Retired" v={conn.retired_at ? conn.retired_at.slice(0, 16).replace("T", " ") : <span className="text-white/30">—</span>} />
                        <Row k="Last test" v={conn.last_test_at ? (
                            <span className={conn.last_test_success ? "text-emerald-200" : "text-rose-200"}>
                                {conn.last_test_success ? "ok" : "fail"} · {conn.last_test_at.slice(0, 16).replace("T", " ")}
                            </span>
                        ) : <span className="text-white/30">—</span>} />
                    </Card>
                </div>
                {!immutable && canEdit && (
                    <button onClick={() => setEdit(true)}
                        data-testid="wizard-general-edit"
                        className="px-3 py-1.5 text-xs border border-white/15 rounded-md hover:bg-white/5">
                        Edit
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className="space-y-3" data-testid="wizard-general-edit-form">
            <div className="grid md:grid-cols-2 gap-3">
                <Field label="Name" required>
                    <input type="text" value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        data-testid="general-edit-name" className={inputCls} />
                </Field>
                <Field label="Environment">
                    <select value={form.environment}
                        onChange={(e) => setForm({ ...form, environment: e.target.value })}
                        data-testid="general-edit-environment" className={inputCls}>
                        <option value="dev">Development</option>
                        <option value="stage">Stage</option>
                        <option value="prod">Production</option>
                    </select>
                </Field>
                <Field label="Base URL" required>
                    <input type="url" value={form.base_url}
                        onChange={(e) => setForm({ ...form, base_url: e.target.value })}
                        data-testid="general-edit-base-url"
                        className={`${inputCls} font-mono`} />
                </Field>
                <Field label="Auth type">
                    <select value={form.auth_type}
                        onChange={(e) => setForm({ ...form, auth_type: e.target.value })}
                        data-testid="general-edit-auth-type" className={inputCls}>
                        {AUTH_TYPES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                    </select>
                </Field>
                <Field label="Default timeout (s)">
                    <input type="number" min={1} max={120} value={form.default_timeout_s}
                        onChange={(e) => setForm({ ...form, default_timeout_s: e.target.value })}
                        data-testid="general-edit-timeout" className={inputCls} />
                </Field>
                <Field label="Tags (comma-separated)">
                    <input type="text" value={form.tags}
                        onChange={(e) => setForm({ ...form, tags: e.target.value })}
                        className={inputCls} />
                </Field>
                <div className="md:col-span-2">
                    <Field label="Description">
                        <textarea rows={2} value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            className={inputCls} />
                    </Field>
                </div>
                <Toggle label="TLS required" checked={form.tls_required}
                    onChange={(v) => setForm({ ...form, tls_required: v })}
                    testId="general-edit-tls" />
                <Toggle label="Allow redirects" checked={form.allow_redirects}
                    onChange={(v) => setForm({ ...form, allow_redirects: v })}
                    testId="general-edit-redirects" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setEdit(false)}
                    className="px-3 py-1.5 text-xs border border-white/15 rounded-md hover:bg-white/5">
                    Cancel
                </button>
                <button onClick={save} disabled={saving}
                    data-testid="general-edit-save"
                    className="px-3 py-1.5 text-xs bg-white text-black rounded-md disabled:opacity-40">
                    {saving ? "Saving…" : "Save changes"}
                </button>
            </div>
        </div>
    );
}
